import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  AutoComplete, Checkbox, Col, DatePicker, Input,
  InputNumber, Row, Select, Form,
} from 'antd';
import moment from 'moment';
import locale from 'antd/lib/date-picker/locale/pt_BR';
import './DynamicFields.scss';

const dateFormat = 'DD/MM/YYYY';

const DynamicFields = (props): JSX.Element => {
  const { field: fieldToRender } = props;
  const { edit } = props;
  const [allState, setAllState] = useState({});

  const validateDateRange = (rule, value, initialDate, finalDate, comparison): Promise<void> => {
    if (!value) {
      return Promise.reject(new Error('O campo é obrigatório'));
    }

    if (comparison === 'LESS_EQUAL') {
      if (!(moment(value).endOf('day') <= moment(finalDate, dateFormat).endOf('day'))) {
        return Promise.reject(new Error(`Insira uma data menor ou igual a ${finalDate}`));
      }
    }

    if (comparison === 'LESS') {
      if (!(moment(value).endOf('day') < moment(finalDate, dateFormat).endOf('day'))) {
        return Promise.reject(new Error(`Insira uma data menor que ${finalDate}`));
      }
    }

    if (comparison === 'GREATER_EQUAL') {
      if (!(moment(value).endOf('day') >= moment(initialDate, dateFormat).endOf('day'))) {
        return Promise.reject(new Error(`Insira uma data maior ou igual que ${initialDate}`));
      }
    }

    if (comparison === 'GREATER') {
      if (!(moment(value).endOf('day') > moment(initialDate, dateFormat).endOf('day'))) {
        return Promise.reject(new Error(`Insira uma data maior que ${initialDate}`));
      }
    }

    if (comparison === 'BETWEEN') {
      if (
        !(moment(value).endOf('day') >= moment(initialDate, dateFormat).endOf('day')
          && moment(value).endOf('day') <= moment(finalDate, dateFormat).endOf('day'))
      ) {
        return Promise.reject(new Error(`Insira uma data entre ${initialDate} e ${finalDate}`));
      }
    }

    return Promise.resolve();
  };

  const validateRange = (rule, value, min, max, localComparison): Promise<void> => {
    if (localComparison == 'GREATER') {
      if (value > max || value <= min) {
        // Check for NaN by checking for self-equality
        if (!max) {
          return Promise.reject(new Error(`Por favor, insira um valor maior que ${min}`));
        }
        return Promise.reject(new Error(`Por favor, insira um valor entre ${min} e ${max}`));
      }
    }
    if (value > max || value < min) {
      // Check for NaN by checking for self-equality
      if (!max) {
        return Promise.reject(new Error(`Por favor, insira um valor maior que ${min}`));
      }
      return Promise.reject(new Error(`Por favor, insira um valor entre ${min} e ${max}`));
    }

    return Promise.resolve();
  };

  const testConditional = (referenceValue, comparison, expectedValue, type): any => {
    if (type === 'LONG') { }

    if (type === 'BOOLEAN') { }

    if (type === 'DATE') { }

    if (type === 'STRING') { }

    if (comparison === 'IN') {
      const objExpected = JSON.parse(expectedValue);
      return objExpected.some((obj) => obj === referenceValue);
    }

    if (comparison === 'EQUAL') {
      return referenceValue.toString() === expectedValue.toString();
    }

    if (comparison === 'GREATER') {
      return referenceValue > expectedValue;
    }

    if (comparison === 'GREATER_EQUAL') {
      return referenceValue >= expectedValue;
    }

    if (comparison === 'LESS') {
      return referenceValue < expectedValue;
    }

    if (comparison === 'LESS_EQUAL') {
      return referenceValue <= expectedValue;
    }

    if (comparison === 'BETWEEN') {
      const expectedValueArray = expectedValue.split(';');
      return referenceValue <= expectedValueArray[0] && expectedValueArray[1] <= referenceValue;
    }

    return true;
  };

  const renderSelectField = (field): JSX.Element => {
    const { type } = props;
    const rules = [];

    if (field.required) {
      rules.push({ required: true, message: 'O campo é obrigatório' });
    }

    return (
      <Row gutter={24} key={field.id} className="inline-field">
        <Col span={24}>
          <Form.Item
            label={<span className="field-title-text">{field.name}</span>}
            name={`${type}${field.id}`}
            rules={rules}
            initialValue={field.value || undefined}
          >
            <Select style={{ width: '100%' }} disabled={edit}>
              {
                field.referenceTableValues.map((v) => (
                  <Select.Option key={v} value={v}>
                    {v}
                  </Select.Option>
                ))
              }
            </Select>
          </Form.Item>
        </Col>
      </Row>
    );
  };

  const renderAutocomplete = (field): JSX.Element => {
    const { type } = props;

    const rules = [];

    if (field.required) {
      rules.push({ required: true, message: 'O campo é obrigatório' });
    }

    const autocompleteFind = (text): any => (
      field.referenceTableValues
        .filter((v) => v.toLowerCase().includes(text.toLowerCase())).map((v) => (
          { label: v, value: v, key: v }
        ))
    );

    const onSearch = (searchText): any => {
      setAllState((previous) => {
        return ({
          ...previous,
          [`options${field.id}`]: !searchText ? [] : autocompleteFind(searchText),
        });
      });

      return field.referenceTableValues;
    };

    return (
      <Row gutter={24} key={field.id} className="inline-field">
        <Col span={24}>
          <Form.Item
            label={<span className="field-title-text">{field.name}</span>}
            name={`${type}${field.id}`}
            rules={rules}
            initialValue={field.value || undefined}
          >
            <AutoComplete
              options={allState[`options${field.id}`] || []}
              onSearch={onSearch}
              disabled={edit}
            />
          </Form.Item>
        </Col>
      </Row>
    );
  };

  const renderNumberField = (field): JSX.Element => {
    const rules = [];
    const { type } = props;

    let min = false;
    let max = false;

    if (field.localExpectedValue) {
      min = Number(field.localExpectedValue.split(';')[0]);
      max = Number(field.localExpectedValue.split(';')[1]);
    }

    if (
      field.idCode === 'GEOFERT-GRID-GRID_TAMANHO_DESEJADO'
      && field.typeValue === 'LONG'
    ) {
      rules.push(
        {
          pattern: /^[\d]{0,19}$/,
          message: 'São permitidos apenas 19 caracteres.',
        },
      );
    }

    if (field.required) {
      if (field.localExpectedValue) {
        rules.push(
          {
            validator: (a, b) => validateRange(a, b, min, max),
          },
          {
            required: true,
            message: ' ',
          },
        );
      } else {
        rules.push({ required: true, message: 'O campo é obrigatório' });
      }
    }

    return (
      <Row gutter={24} key={field.id} className="inline-field">
        <Col span={24}>
          <Form.Item
            label={<span className="field-title-text">{field.name}</span>}
            name={`${type}${field.id}`}
            rules={rules}
            initialValue={field.value || undefined}
          >
            <InputNumber disabled={edit} style={{ width: '100%' }} {...(min ? { min, max } : {})} />
          </Form.Item>
        </Col>
      </Row>
    );
  };

  const renderTextField = (field): JSX.Element => {
    const { type } = props;
    const rules = [];

    if (field.required) {
      rules.push({ required: true, message: 'O campo é obrigatório' });
    }

    return (
      <Row gutter={24} key={field.id} className="inline-field">
        <Col span={24}>
          <Form.Item
            label={<span className="field-title-text">{field.name}</span>}
            name={`${type}${field.id}`}
            rules={rules}
            initialValue={field.value || undefined}
          >
            <Input style={{ width: '100%' }} disabled={edit} />
          </Form.Item>
        </Col>
      </Row>
    );
  };

  const renderDateField = (field): JSX.Element => {
    const { type } = props;
    const rules = [];

    let initialDate = null;
    let finalDate = null;

    if (field.localExpectedValue) {
      const convertDateString = (d): string => moment(d, 'YYYY-MM-DD').format('DD/MM/YYYY');

      if (field.localComparison === 'GREATER_EQUAL' || field.localComparison === 'GREATER') {
        initialDate = convertDateString(field.localExpectedValue);
      }

      if (field.localComparison === 'LESS_EQUAL' || field.localComparison === 'LESS') {
        finalDate = convertDateString(field.localExpectedValue);
      }

      if (field.localComparison === 'BETWEEN') {
        initialDate = convertDateString(field.localExpectedValue.split(';')[0]);
        finalDate = convertDateString(field.localExpectedValue.split(';')[1]);
      }
    }

    if (field.required) {
      if (field.localExpectedValue) {
        rules.push(
          {
            validator: (a, b) => validateDateRange(
              a, b, initialDate, finalDate, field.localComparison,
            ),
          },
          {
            required: true,
            message: ' ',
          },
        );
      } else {
        rules.push({ required: true, message: 'O campo é obrigatório' });
      }
    }

    return (
      <Row gutter={24} key={field.id} className="inline-field">
        <Col span={24}>
          <Form.Item
            label={<span className="field-title-text">{field.name}</span>}
            name={`${type}${field.id}`}
            rules={rules}
            initialValue={field.value ? moment(field.value, dateFormat) : undefined}
          >
            <DatePicker
              style={{ width: '100%' }}
              locale={locale}
              format={dateFormat}
              disabled={edit}
            />
          </Form.Item>
        </Col>
      </Row>
    );
  };

  const renderCheckField = (field): JSX.Element => {
    const rules = [];

    if (field.required) {
      rules.push({ required: true, message: 'O campo é obrigatório' });
    }

    const { type } = props;

    return (
      <Row gutter={24} key={field.id}>
        <Col span={24}>
          <Form.Item
            name={`${type}${field.id}`}
            initialValue={field.value === 'true' || field.value === 'Sim'}
            valuePropName="checked"
            rules={rules}
          >
            <Checkbox
              disabled={edit}
              className="field-title-text"
            >
              {field.name}
            </Checkbox>
          </Form.Item>
        </Col>
      </Row>
    );
  };

  const renderDynamicField = (field): JSX.Element => {
    let actualField;
    const { type } = props;

    switch (true) {
      case (field.referenceTableValues.length > 0):
        actualField = field.id === 1 ? renderAutocomplete(field) : renderSelectField(field);
        break;

      case (field.typeValue === 'LONG'):
        actualField = renderNumberField(field);
        break;

      case (field.typeValue === 'DATE'):
        actualField = renderDateField(field);
        break;

      case (field.typeValue === 'BOOLEAN'):
        actualField = renderCheckField(field);
        break;

      default:
        actualField = renderTextField(field);
        break;
    }

    return (
      <React.Fragment key={field.id}>
        {
          field.conditionFieldId
            ? (
              <Form.Item
                shouldUpdate
                noStyle
              >
                {
                  (formInstanceParameter): any => {
                    const referenceValue = formInstanceParameter.getFieldValue(`${type}${field.conditionFieldId}`);

                    if (
                      !referenceValue
                      || !testConditional(
                        referenceValue,
                        field.conditionComparison,
                        field.conditionExpectedValue,
                        field.type,
                      )
                    ) {
                      return null;
                    }

                    return actualField;
                  }
                }
              </Form.Item>
            ) : actualField
        }
      </React.Fragment>
    );
  };

  return (
    <div className="DynamicFields">
      {renderDynamicField(fieldToRender)}
    </div>
  );
};

DynamicFields.propTypes = {
  form: PropTypes.objectOf(PropTypes.any).isRequired,
  field: PropTypes.objectOf(PropTypes.any).isRequired,
  type: PropTypes.string.isRequired,
  edit: PropTypes.bool,
};

DynamicFields.defaultProps = {};

export default DynamicFields;
